import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Chip,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button
} from '@mui/material';
import {
  FiberManualRecord as RecordIcon
} from '@mui/icons-material';
import io from 'socket.io-client';

import WebRTCService from '../../services/webrtcService';
import PreJoinRoom from './PreJoinRoom';
import VideoGrid from './VideoGrid';
import MeetingControls from './MeetingControls';
import ParticipantsPanel from './ParticipantsPanel';
import ChatPanel from './ChatPanel';
import { RecordingManager } from '../../services/recordingService';
import analyticsService from '../../services/analyticsService';
import api from '../../services/authService';

const SOCKET_URL = process.env.REACT_APP_API_URL ? 
  process.env.REACT_APP_API_URL.replace('/api', '') : 
  window.location.origin;

function MeetingRoomEnhanced() {
  const { meetingId } = useParams();
  const navigate = useNavigate();

  // Meeting state
  const [meeting, setMeeting] = useState(null);
  const [participant, setParticipant] = useState(null);
  const [participants, setParticipants] = useState([]);
  
  // Connection state
  const [socket, setSocket] = useState(null);
  const [webrtcService, setWebrtcService] = useState(null);
  const [deviceService, setDeviceService] = useState(null);
  
  // Media state
  const [localStream, setLocalStream] = useState(null);
  const [remoteStreams, setRemoteStreams] = useState(new Map());
  const [screenShareStream, setScreenShareStream] = useState(null);
  
  // UI state
  const [showPreJoin, setShowPreJoin] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [isCameraOff, setIsCameraOff] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [isHandRaised, setIsHandRaised] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  
  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const recordingManagerRef = useRef(null);
  
  // Error/notification state
  const [error, setError] = useState('');
  const [notification, setNotification] = useState('');
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  
  // WebRTC state
  const [activeSpeakerId, setActiveSpeakerId] = useState(null);
  const [audioLevels, setAudioLevels] = useState(new Map());

  // Set document title
  useEffect(() => {
    if (meeting?.title) {
      document.title = `${meeting.title} - EchoNote`;
    } else {
      document.title = 'Meeting Room - EchoNote';
    }
  }, [meeting?.title]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Active speaker detection
  useEffect(() => {
    if (!webrtcService) return;

    const interval = setInterval(() => {
      const activeSpeaker = webrtcService.getActiveSpeaker();
      setActiveSpeakerId(activeSpeaker);
      setAudioLevels(new Map(webrtcService.audioLevels));
    }, 500);

    return () => clearInterval(interval);
  }, [webrtcService]);

  const cleanup = () => {
    // Stop analytics collection
    analyticsService.stop();
    
    if (socket) {
      socket.disconnect();
    }
    if (localStream) {
      localStream.getTracks().forEach(track => track.stop());
    }
    if (screenShareStream) {
      screenShareStream.getTracks().forEach(track => track.stop());
    }
    if (webrtcService) {
      webrtcService.cleanup();
    }
    if (recordingManagerRef.current) {
      recordingManagerRef.current.cleanup();
    }
  };

  const handlePreJoinComplete = async (preJoinData) => {
    setLocalStream(preJoinData.stream);
    setDeviceService(preJoinData.deviceService);
    setIsMuted(preJoinData.isMuted);
    setIsCameraOff(preJoinData.isCameraOff);
    setShowPreJoin(false);

    // Join the meeting
    await joinMeeting(null, preJoinData.displayName);
  };

  const joinMeeting = async (meetingPassword = null, displayName = null) => {
    try {
      // Join the meeting
      const response = await api.post(
        `/hosted-meetings/${meetingId}/join`,
        { 
          displayName,
          password: meetingPassword 
        }
      );
      
      setMeeting(response.data.meeting);
      setParticipant(response.data.participant);
      setShowPasswordDialog(false);
      setPasswordError('');

      // Initialize analytics
      analyticsService.init(response.data.meeting.id, response.data.participant.userId);
      analyticsService.trackEvent('participant_joined', {
        displayName: response.data.participant.displayName,
        role: response.data.participant.role
      });

      // Initialize WebRTC service
      const webrtc = new WebRTCService(null, localStream);
      setWebrtcService(webrtc);

      // Handle remote streams
      webrtc.onRemoteStream((peerId, stream, trackKind) => {
        setRemoteStreams(prev => new Map(prev).set(peerId, stream));
      });

      webrtc.onRemoteTrackRemoved((peerId, trackKind) => {
        // Handle track removal if needed
        console.log(`Track ${trackKind} removed from ${peerId}`);
      });

      // Connect to socket
      const token = localStorage.getItem('token');
      const newSocket = io(SOCKET_URL, {
        auth: { token }
      });

      newSocket.on('connect', () => {
        console.log('Socket connected');
        webrtc.socket = newSocket;
        
        newSocket.emit('join-meeting', {
          meetingId: response.data.meeting.id,
          participantId: response.data.participant.id
        });
      });

      newSocket.on('connect_error', (err) => {
        console.error('Socket connection error:', err.message);
        setError('Failed to connect to meeting server: ' + err.message);
      });

      newSocket.on('error', (data) => {
        console.error('Socket error:', data.message);
        setError(data.message);
      });

      // Handle TURN credentials
      newSocket.on('turn-credentials', (turnConfig) => {
        webrtc.setTurnConfig(turnConfig);
      });

      // Handle existing participants
      newSocket.on('existing-participants', async (data) => {
        setParticipants(data.participants);
        
        // Create WebRTC connections for existing participants
        for (const p of data.participants) {
          if (p.peerId) {
            const offer = await webrtc.createOffer(p.peerId);
            newSocket.emit('webrtc-offer', {
              to: p.peerId,
              offer
            });
          }
        }
      });

      // Handle new participant joined
      newSocket.on('user-joined', async (data) => {
        setParticipants(prev => [...prev, data]);
        setNotification(`${data.displayName} joined the meeting`);
        analyticsService.trackEvent('participant_joined', {
          displayName: data.displayName,
          participantId: data.participantId
        });
      });

      // Handle participant left
      newSocket.on('user-left', (data) => {
        setParticipants(prev => prev.filter(p => p.participantId !== data.participantId));
        setRemoteStreams(prev => {
          const newMap = new Map(prev);
          newMap.delete(data.peerId);
          return newMap;
        });
        webrtc.closePeerConnection(data.peerId);
        setNotification(`${data.displayName || 'A participant'} left the meeting`);
        analyticsService.trackEvent('participant_left', {
          displayName: data.displayName,
          participantId: data.participantId
        });
      });

      // Handle participant state updates
      newSocket.on('participant-state-updated', (data) => {
        setParticipants(prev => 
          prev.map(p => p.participantId === data.participantId ? { ...p, ...data } : p)
        );
      });

      // Handle WebRTC signaling
      newSocket.on('webrtc-offer', async (data) => {
        const answer = await webrtc.handleOffer(data.from, data.offer);
        newSocket.emit('webrtc-answer', {
          to: data.from,
          answer
        });
      });

      newSocket.on('webrtc-answer', async (data) => {
        await webrtc.handleAnswer(data.from, data.answer);
        
        // Start collecting stats once we have a peer connection
        const peerConnection = webrtc.peers.get(data.from);
        if (peerConnection && !analyticsService.statsCollectionInterval) {
          analyticsService.startStatsCollection(peerConnection);
        }
      });

      newSocket.on('webrtc-ice-candidate', async (data) => {
        await webrtc.handleIceCandidate(data.from, data.candidate);
      });

      // Handle chat messages
      newSocket.on('chat-message', (message) => {
        setChatMessages(prev => {
          // Check if this is our own message that we already added optimistically
          const isOwnMessage = message.participantId === response.data.participant.id;
          
          if (isOwnMessage) {
            // Look for a temporary message that matches this server message
            // Match based on: content, participant, and timestamp within 5 seconds
            const messageTime = new Date(message.createdAt).getTime();
            const tempMessageIndex = prev.findIndex(msg => 
              msg.id.toString().startsWith('temp-') &&
              msg.participantId === message.participantId &&
              msg.content === message.content &&
              msg._tempTimestamp &&
              Math.abs(messageTime - msg._tempTimestamp) < 5000 // Within 5 seconds
            );
            
            if (tempMessageIndex !== -1) {
              // Replace the temporary message with the real one
              const newMessages = [...prev];
              newMessages[tempMessageIndex] = message;
              return newMessages;
            }
          }
          
          // Add new message (either from another participant or own message without temp version)
          return [...prev, message];
        });
        
        // Only increment unread count for messages from other participants
        if (message.participantId !== response.data.participant.id && !showChat) {
          setUnreadChatCount(prev => prev + 1);
        }
      });

      // Handle recording events
      newSocket.on('recording-started', (data) => {
        setMeeting(prev => ({ ...prev, recordingEnabled: true, recordingStartedAt: data.startedAt }));
        setNotification('Recording started');
      });

      newSocket.on('recording-stopped', () => {
        setMeeting(prev => ({ ...prev, recordingEnabled: false }));
        setNotification('Recording stopped');
      });

      // Handle host actions
      newSocket.on('host-mute-all', () => {
        toggleMute(true);
        setNotification('Host muted all participants');
      });

      newSocket.on('removed-by-host', () => {
        setNotification('You were removed from the meeting');
        setTimeout(() => navigate('/host-dashboard'), 2000);
      });

      newSocket.on('meeting-ended-by-host', () => {
        setNotification('Meeting ended by host');
        setTimeout(() => navigate('/host-dashboard'), 2000);
      });

      newSocket.on('meeting-lock-changed', (data) => {
        setMeeting(prev => ({ ...prev, isLocked: data.locked }));
        setNotification(data.locked ? 'Meeting locked' : 'Meeting unlocked');
      });

      setSocket(newSocket);

      // Auto-start recording if host
      if (response.data.participant.role === 'host' && response.data.meeting.recordingEnabled) {
        try {
          await startRecording();
        } catch (err) {
          console.error('Error starting recording:', err);
        }
      }
    } catch (err) {
      console.error('Error joining meeting:', err);
      
      if (err.response && err.response.status === 403 && 
          err.response.data.error === 'Invalid meeting password') {
        setShowPasswordDialog(true);
        setPasswordError('Invalid password. Please try again.');
      } else {
        setError(err.response?.data?.error || 'Failed to join meeting');
      }
    }
  };

  const toggleMute = (forceMute = null) => {
    if (localStream) {
      const audioTrack = localStream.getAudioTracks()[0];
      if (audioTrack) {
        const shouldMute = forceMute !== null ? forceMute : !isMuted;
        audioTrack.enabled = !shouldMute;
        setIsMuted(shouldMute);
        if (socket) {
          socket.emit('update-participant-state', { isMuted: shouldMute });
        }
      }
    }
  };

  const toggleCamera = async () => {
    if (localStream) {
      const videoTrack = localStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsCameraOff(!videoTrack.enabled);
        if (socket) {
          socket.emit('update-participant-state', { isCameraOff: !videoTrack.enabled });
        }
      }
    }
  };

  const toggleScreenShare = async () => {
    try {
      if (!isScreenSharing) {
        if (!deviceService) {
          setError('Device service not initialized');
          return;
        }

        const screenStream = await deviceService.getDisplayMedia({
          video: true,
          audio: false
        });
        
        setScreenShareStream(screenStream);
        setIsScreenSharing(true);

        if (webrtcService && socket) {
          const screenTrack = screenStream.getVideoTracks()[0];
          await webrtcService.addTrack(screenTrack, screenStream);
          socket.emit('update-participant-state', { isScreenSharing: true });
          analyticsService.trackEvent('screen_share_started');
        }
        
        screenStream.getVideoTracks()[0].onended = () => {
          stopScreenShare();
        };
      } else {
        stopScreenShare();
      }
    } catch (err) {
      console.error('Error sharing screen:', err);
      setError('Failed to share screen: ' + err.message);
    }
  };

  const stopScreenShare = () => {
    if (screenShareStream) {
      const screenTrack = screenShareStream.getVideoTracks()[0];
      if (webrtcService) {
        webrtcService.removeTrack(screenTrack);
      }
      screenShareStream.getTracks().forEach(track => track.stop());
      setScreenShareStream(null);
    }
    
    setIsScreenSharing(false);
    if (socket) {
      socket.emit('update-participant-state', { isScreenSharing: false });
      analyticsService.trackEvent('screen_share_stopped');
    }
  };

  const toggleHand = () => {
    const newState = !isHandRaised;
    setIsHandRaised(newState);
    if (socket) {
      socket.emit('update-participant-state', { isHandRaised: newState });
      analyticsService.trackEvent(newState ? 'hand_raised' : 'hand_lowered');
    }
  };

  const handleSendMessage = (content) => {
    if (socket && participant) {
      // Create timestamp for unique identification
      const timestamp = Date.now();
      
      // Optimistically add message to UI immediately
      const optimisticMessage = {
        id: `temp-${timestamp}`,
        content,
        createdAt: new Date(timestamp).toISOString(),
        participantId: participant.id,
        participant: {
          id: participant.id,
          displayName: participant.displayName
        },
        _tempTimestamp: timestamp // Track when this temporary message was created
      };
      setChatMessages(prev => [...prev, optimisticMessage]);
      
      // Send to server
      socket.emit('chat-message', {
        content,
        recipientId: null
      });
      analyticsService.trackEvent('chat_sent');
    }
  };

  const toggleChat = () => {
    setShowChat(!showChat);
    if (!showChat) {
      setUnreadChatCount(0);
    }
  };

  const toggleParticipants = () => {
    setShowParticipants(!showParticipants);
  };

  const startRecording = async () => {
    try {
      if (!recordingManagerRef.current) {
        recordingManagerRef.current = new RecordingManager();
        await recordingManagerRef.current.initialize();
      }
      
      recordingManagerRef.current.start();
      setIsRecording(true);
      if (socket) {
        socket.emit('start-recording');
      }
    } catch (err) {
      console.error('Error starting recording:', err);
      throw err;
    }
  };

  const stopAndSaveRecording = async () => {
    if (!recordingManagerRef.current || !recordingManagerRef.current.isRecording()) {
      return;
    }

    try {
      const audioBlob = await recordingManagerRef.current.stop();
      setIsRecording(false);
      
      const reader = new FileReader();
      const base64Data = await new Promise((resolve, reject) => {
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(audioBlob);
      });

      const duration = recordingManagerRef.current.getDuration();
      const filename = `recording-${Date.now()}.webm`;

      await api.post(
        `/hosted-meetings/${meetingId}/upload-recording`,
        {
          recordingData: base64Data,
          filename,
          duration,
          format: 'webm'
        }
      );

      if (socket) {
        socket.emit('stop-recording');
      }
    } catch (err) {
      console.error('Error saving recording:', err);
      setError('Failed to save recording: ' + err.message);
    }
  };

  const endMeeting = async () => {
    if (!window.confirm('Are you sure you want to end this meeting for everyone?')) return;
    
    try {
      if (isRecording) {
        await stopAndSaveRecording();
      }
      
      await api.post(
        `/hosted-meetings/${meetingId}/end`,
        {}
      );
      
      cleanup();
      navigate('/host-dashboard');
    } catch (err) {
      console.error('Error ending meeting:', err);
      setError('Failed to end meeting');
    }
  };

  const leaveMeeting = async () => {
    try {
      await api.post(
        `/hosted-meetings/${meetingId}/leave`,
        {}
      );
      
      cleanup();
      navigate('/host-dashboard');
    } catch (err) {
      console.error('Error leaving meeting:', err);
      cleanup();
      navigate('/host-dashboard');
    }
  };

  const handlePasswordSubmit = () => {
    if (password.trim()) {
      joinMeeting(password);
    } else {
      setPasswordError('Password is required');
    }
  };

  const handlePasswordCancel = () => {
    setShowPasswordDialog(false);
    setPassword('');
    setPasswordError('');
    navigate('/host-dashboard');
  };

  // Show pre-join screen
  if (showPreJoin) {
    return (
      <PreJoinRoom
        meetingTitle={meeting?.title || 'Meeting'}
        onJoin={handlePreJoinComplete}
        onCancel={() => navigate('/host-dashboard')}
      />
    );
  }

  // Show loading while joining
  if (!meeting || !participant) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography>Loading meeting...</Typography>
      </Box>
    );
  }

  // Build local participant object for UI
  const localParticipant = {
    ...participant,
    displayName: participant.displayName || 'You',
    isMuted,
    isCameraOff,
    isScreenSharing,
    isHandRaised
  };

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#1a1a1a' }}>
      {/* Header */}
      <Box 
        sx={{ 
          p: 2, 
          bgcolor: '#2a2a2a', 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '1px solid #444'
        }}
      >
        <Typography variant="h6" sx={{ color: 'white' }}>
          {meeting.title}
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {meeting.recordingEnabled && (
            <Chip 
              icon={<RecordIcon />} 
              label="Recording" 
              color="error" 
              size="small"
            />
          )}
          {meeting.isLocked && (
            <Chip 
              label="Locked" 
              color="warning" 
              size="small"
            />
          )}
        </Box>
      </Box>

      {/* Video Grid */}
      <VideoGrid
        localParticipant={localParticipant}
        localStream={localStream}
        remoteParticipants={participants}
        remoteStreams={remoteStreams}
        activeSpeakerId={activeSpeakerId}
        audioLevels={audioLevels}
      />

      {/* Controls */}
      <MeetingControls
        isMuted={isMuted}
        isCameraOff={isCameraOff}
        isScreenSharing={isScreenSharing}
        isHandRaised={isHandRaised}
        isRecording={isRecording}
        isHost={participant.role === 'host'}
        unreadChatCount={unreadChatCount}
        onToggleMute={() => toggleMute()}
        onToggleCamera={toggleCamera}
        onToggleScreenShare={toggleScreenShare}
        onToggleHand={toggleHand}
        onToggleChat={toggleChat}
        onToggleParticipants={toggleParticipants}
        onStartRecording={startRecording}
        onStopRecording={stopAndSaveRecording}
        onEndMeeting={endMeeting}
        onLeaveMeeting={leaveMeeting}
      />

      {/* Participants Panel */}
      <ParticipantsPanel
        open={showParticipants}
        onClose={() => setShowParticipants(false)}
        participants={participants}
        localParticipant={localParticipant}
        isHost={participant.role === 'host'}
      />

      {/* Chat Panel */}
      <ChatPanel
        open={showChat}
        onClose={() => setShowChat(false)}
        messages={chatMessages}
        onSendMessage={handleSendMessage}
        currentParticipantId={participant.id}
      />

      {/* Password Dialog */}
      <Dialog open={showPasswordDialog} onClose={handlePasswordCancel}>
        <DialogTitle>Meeting Password Required</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Password"
            type="password"
            fullWidth
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={!!passwordError}
            helperText={passwordError}
            onKeyPress={(e) => e.key === 'Enter' && handlePasswordSubmit()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePasswordCancel}>Cancel</Button>
          <Button onClick={handlePasswordSubmit} variant="contained">Join</Button>
        </DialogActions>
      </Dialog>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setError('')} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      {/* Notification Snackbar */}
      <Snackbar
        open={!!notification}
        autoHideDuration={3000}
        onClose={() => setNotification('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        sx={{ mb: 10 }}
      >
        <Alert onClose={() => setNotification('')} severity="info" sx={{ width: '100%' }}>
          {notification}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default MeetingRoomEnhanced;
